import { useEffect, useState } from 'react';
import SDK from '../../../Components/SDK';
import { DefaultProfilePicture } from '../../rootAccess';
import logger from '../../../Helpers/logger';

// Unified cache for all data
const cache = {};

const generateKey = (imageUri, userId) => {
    if (typeof imageUri === 'string') return `${userId}-${imageUri}`;
    if (imageUri instanceof Blob) return `${userId}-blob-${imageUri.size}-${imageUri.type}`;
    return `${userId}-unknown`;
};

export const useProfileImage = ({ imageUri = '', userId = '', defaultImg = DefaultProfilePicture, setImageSource }) => {
    const [src, setSrc] = useState(defaultImg);
    const [loading, setLoading] = useState(!!imageUri);

    useEffect(() => {
        let isMounted = true;
        const key = generateKey(imageUri, userId);

        const cleanupBlob = () => {
            const prevKey = cache[`prevKey-${userId}`];
            if (prevKey && prevKey !== key) {
                const blobUrl = cache[`blobUrl-${prevKey}`];
                if (blobUrl) {
                    URL.revokeObjectURL(blobUrl);
                    delete cache[`blobUrl-${prevKey}`];
                }
            }
            cache[`prevKey-${userId}`] = key;
        };

        const resolveImage = async () => {
            try {
                setLoading(true);

                if (!imageUri) {
                    if (isMounted) setSrc(defaultImg);
                    return;
                }

                cleanupBlob();

                if (cache[key]) {
                    if (isMounted) setSrc(cache[key]);
                    setImageSource?.(cache[key]);
                    return;
                }

                if (typeof imageUri === 'string' && imageUri.startsWith('http')) {
                    cache[key] = imageUri;
                    if (isMounted) setSrc(imageUri);
                    return;
                }

                if (imageUri instanceof Blob) {
                    if (imageUri.size > 0 && imageUri.type.startsWith('image/')) {
                        const blobUrl = URL.createObjectURL(imageUri);
                        cache[`blobUrl-${key}`] = blobUrl;
                        cache[key] = blobUrl;
                        if (isMounted) setSrc(blobUrl);
                        setImageSource?.(blobUrl);
                    } else if (isMounted) {
                        setSrc(defaultImg);
                    }
                    return;
                }

                if (typeof imageUri === 'string') {
                    const res = await SDK.getMediaURL(imageUri);
                    if (res.statusCode === 200 && res.data?.blob) {
                        const blob = res.data.blob;
                        if (blob.size > 0 && blob.type.startsWith('image/')) {
                            const blobUrl = URL.createObjectURL(blob);
                            cache[`blobUrl-${key}`] = blobUrl;
                            cache[key] = blobUrl;
                            if (isMounted) setSrc(blobUrl);
                            setImageSource?.(blobUrl);
                        } else if (isMounted) {
                            setSrc(defaultImg);
                        }
                    } else if (res.statusCode === 200 && res.data?.blobUrl) {
                        cache[key] = res.data.blobUrl;
                        if (isMounted) setSrc(res.data.blobUrl);
                        setImageSource?.(res.data.blobUrl);
                    } else if (isMounted) {
                        setSrc(defaultImg);
                    }
                }
            } catch (err) {
                logger.error('Image fetch error:', err);
                if (isMounted) setSrc(defaultImg);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        resolveImage();

        return () => {
            isMounted = false;
        };
    }, [imageUri, userId, defaultImg]);

    return { src, loading };
};
